import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Container,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
    Close,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import en_ja from "../../translations/en_ja";

const theme = createTheme({
    palette: {
        primary: {
            main: "#ff0000",
        },
    },
});

const SuperAdminSettings = () => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    // Modal states
    const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [showOldPassword, setShowOldPassword] = useState(false);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [userId, setUserId] = useState(null);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setUserId(user?._id || user?.id);
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbarType("success");
        setSnackbarOpen(false);
        setSnackbarMessage("");
    };

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user?._id || user?.id) {
            setUserId(user?._id || user?.id);
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.oldPassword.trim()) {
            newErrors.oldPassword = en_ja["Old password is required"];
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = en_ja["New password is required"];
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = en_ja["Password must be at least 6 characters"];
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = en_ja["Confirm password is required"];
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = en_ja["Passwords do not match"];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const changePassword = async () => {
        if (!validateForm()) return;
        if (!userId) return;

        setLoading(true);

        try {
            const response = await api.put(
                `/v1/super-admin/change-password/${userId}`,
                {
                    oldPassword: formData.oldPassword,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword,
                }
            );

            if (response.status === 200) {
                setSnackbarMessage(
                    response?.data?.message ||
                    en_ja["Password changed successfully!"]
                );
                setSnackbarType("success");
                setSnackbarOpen(true);

                handleCloseModal();
            }
        } catch (error) {

            // prevent redirect/logout behavior
            if (error?.response?.status === 401) {
                setSnackbarMessage(
                    error?.response?.data?.message ||
                    en_ja["Invalid old password"]
                );
            } else {
                setSnackbarMessage(
                    error?.response?.data?.message ||
                    en_ja["Failed to change password. Please try again."]
                );
            }

            setSnackbarType("error");
            setSnackbarOpen(true);

            return; // stop further propagation
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        changePassword();
    };

    const handleOpenPasswordChange = () => {
        setPasswordChangeOpen(true);
        setFormData({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
        });
        setErrors({});
    };

    const handleCloseModal = () => {
        setPasswordChangeOpen(false);
        setFormData({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
        });
        setErrors({});
    };

    const renderResetPasswordStep = () => (
        <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>

                <Typography variant="h5" sx={{ fontWeight: 600, color: "#1a1a1a", flex: 1, textAlign: "center" }}>
                    {en_ja["Reset Password"]}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 4, color: "#666666", textAlign: "center", fontSize: "14px" }}>
                {en_ja["Please enter your old password and set a new password."]}
            </Typography>

            <form onSubmit={handleSubmit}>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: "#1a1a1a",
                            fontWeight: 500,
                            fontSize: "14px",
                        }}
                    >
                        {en_ja["Old Password"]}
                    </Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        name="oldPassword"
                        placeholder={en_ja["Enter your old password"]}
                        type={showOldPassword ? "text" : "password"}
                        value={formData.oldPassword}
                        onChange={handleInputChange}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showOldPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                fontSize: "14px",
                                "& fieldset": {
                                    borderColor: "#e0e0e0",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#ff0000",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#ff0000",
                                },
                            },
                        }}
                    />
                </Box>

                {/* NEW PASSWORD */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: "#1a1a1a",
                            fontWeight: 500,
                            fontSize: "14px",
                        }}
                    >
                        {en_ja["New Password"]}
                    </Typography>
                    <TextField
                        name="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        fullWidth
                        placeholder={en_ja["Enter your new password"]}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showNewPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                fontSize: "14px",
                                "& fieldset": {
                                    borderColor: "#e0e0e0",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#ff0000",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#ff0000",
                                },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: "#1a1a1a",
                            fontWeight: 500,
                            fontSize: "14px",
                        }}
                    >
                        {en_ja["Confirm Password"]}
                    </Typography>
                    <TextField
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        fullWidth
                        placeholder={en_ja["Confirm your new password"]}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                fontSize: "14px",
                                "& fieldset": {
                                    borderColor: "#e0e0e0",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#ff0000",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#ff0000",
                                },
                            },
                        }}
                    />
                </Box>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                        py: 1.5,
                        backgroundColor: "#ff0000",
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "16px",
                        fontWeight: 600,
                        boxShadow: "none",
                        "&:hover": {
                            backgroundColor: "#e60000",
                            boxShadow: "none",
                        },
                        "&:disabled": {
                            backgroundColor: "#cccccc",
                        },
                    }}
                >
                    {loading ? en_ja["Changing Password..."] : en_ja["Confirm Change Password"]}
                </Button>

            </form>

        </>
    );

    return (
        <ThemeProvider theme={theme}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                message={snackbarMessage}
                onClose={handleCloseSnackbar}
                type={snackbarType}
            />

            <Container maxWidth="md" sx={{ py: 4 }}>
                <Card
                    elevation={2}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "#1a1a1a",
                        }}
                    >
                        {en_ja["Password Change"]}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >            <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#666666",
                                    fontSize: "14px",
                                    mb: 0.5,
                                }}
                            >
                                {en_ja["Current Password"]}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#1a1a1a",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                }}
                            >
                                •••••••••••••
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            onClick={handleOpenPasswordChange}
                            sx={{
                                textTransform: "none",
                                borderColor: "#ff0000",
                                color: "#ff0000",
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                                "&:hover": {
                                    borderColor: "#e60000",
                                    backgroundColor: "#fff5f5",
                                },
                            }}
                        >
                            {en_ja["Change Password"]}
                        </Button>
                    </Box>
                </Card>

                <Dialog
                    open={passwordChangeOpen}
                    onClose={handleCloseModal}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 1,
                        },
                    }}
                >
                    <DialogContent sx={{ p: 4, position: "relative" }}>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={{
                                position: "absolute",
                                right: 16,
                                top: 16,
                                color: "#666666",
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>

                        {renderResetPasswordStep()}
                    </DialogContent>
                </Dialog>

            </Container>
        </ThemeProvider>
    );
};

export default SuperAdminSettings

